import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import {
    AI_GENERATION_COMPLETION_STATUS,
    AI_GENERATION_PROGRESS,
} from "../../constants";

import { toast } from "sonner";
import { renderInProgressToast } from '../../../lib/ProgressInfoToastMessage';
import { teamAtom } from "../../stores/jotai/base";
import { aiCanvasGenerationAtom } from "../../stores/jotai/generation-progress/ai/aiCanvasGeneration";
import { aiTemplateGenerationAtom } from "../../stores/jotai/generation-progress/ai/aiTemplateGeneration";
import AlertDialog from "./alert-dialog";
import IwButton from "./IwButton";

const AiGenerationStatus = () => {
    const team = useAtomValue(teamAtom);
    const teamID = team?.groupID;
    const [canvasInProgressSnackbarID, setCanvasInProgressSnackbarID] =
        useState(null);
    const [templateInProgressSnackbarID, setTemplateInProgressSnackbarID] =
        useState(null);
   
    const [aiCanvasGenerationState, setAiCanvasGenerationState] = useAtom(
        aiCanvasGenerationAtom
    );
    const [aiTemplateGenerationState, setAiTemplateGenerationState] = useAtom(
        aiTemplateGenerationAtom
    );

    const [canvasErrorModalOpen, setCanvasErrorModalOpen] = useState(false);
    const [canvasSuccessModalOpen, setCanvasSuccessModalOpen] = useState(false);

    const [templateGenErrorModalOpen, setTemplateGenErrorModalOpen] = useState(false);
    const [templateGenSuccessModalOpen, setTemplateGenSuccessModalOpen] = useState(false);

    const aiCanvasGenerationProgress =
        aiCanvasGenerationState[teamID]?.aiCanvasGenerationProgress || null;
    const aiCanvasGenerationCompletionStatus =
        aiCanvasGenerationState[teamID]?.aiCanvasGenerationCompletionStatus ||
        null;
    const aiTemplateGenerationProgress =
        aiTemplateGenerationState[teamID]?.aiTemplateGenerationProgress || null;
    const aiTemplateGenerationCompletionStatus =
        aiTemplateGenerationState[teamID]
            ?.aiTemplateGenerationCompletionStatus || null;

    
    const handleCanvasErrorModalClose = () => {
        setAiCanvasGenerationState((prevState) => {
            const newState = { ...prevState }

            newState[teamID] = {
              aiCanvasGenerationCompletionStatus:
                AI_GENERATION_COMPLETION_STATUS.NO_STATUS,
              aiCanvasGenerationProgress:
                AI_GENERATION_PROGRESS.NOT_STARTED
            }

            return newState
          })
          setCanvasErrorModalOpen(false);
    }

    const handleCanvasSuccessModalClose = () => {
        setAiCanvasGenerationState(prevState => {
            const newState = { ...prevState };

            newState[teamID] = {
                aiCanvasGenerationCompletionStatus:
                    AI_GENERATION_COMPLETION_STATUS.NO_STATUS,
                aiCanvasGenerationProgress:
                    AI_GENERATION_PROGRESS.NOT_STARTED,
            };

            return newState;
        });
          setCanvasSuccessModalOpen(false);
    }

    const handleTemplateGenErrorModalClose = () => {
        setAiTemplateGenerationState((prevState) => {
            const newState = { ...prevState }

            newState[teamID] = {
              aiTemplateGenerationCompletionStatus:
                AI_GENERATION_COMPLETION_STATUS.NO_STATUS,
              aiTemplateGenerationProgress:
                AI_GENERATION_PROGRESS.NOT_STARTED
            }

            return newState
          })
          setTemplateGenErrorModalOpen(false);
    }

    const handleTemplateGenSuccessModalClose = () => {
        setAiTemplateGenerationState(prevState => {
            const newState = { ...prevState }

            newState[teamID] = {
                aiTemplateGenerationCompletionStatus:
                    AI_GENERATION_COMPLETION_STATUS.NO_STATUS,
                aiTemplateGenerationProgress:
                    AI_GENERATION_PROGRESS.NOT_STARTED,
            };

            return newState;
        });
          setTemplateGenSuccessModalOpen(false);
    }

    //Canvas generation status
    useEffect(() => {
        if (aiCanvasGenerationProgress === AI_GENERATION_PROGRESS.IN_PROGRESS) {
            const toastID = renderInProgressToast("Creating your canvas. We'll notify you once it's ready!")
            setCanvasInProgressSnackbarID(toastID);
        }
    }, [aiCanvasGenerationProgress]);

    useEffect(() => {
        if (
            aiCanvasGenerationCompletionStatus ===
            AI_GENERATION_COMPLETION_STATUS.FAILED
        ) {
            setCanvasErrorModalOpen(true);
        } else if (
            aiCanvasGenerationCompletionStatus ===
            AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
        ) {
            setCanvasSuccessModalOpen(true);
        }

        if (
            aiCanvasGenerationCompletionStatus !==
            AI_GENERATION_COMPLETION_STATUS.NO_STATUS
        ) {
            setTimeout(() => toast.dismiss(canvasInProgressSnackbarID), 500);
        }
    }, [aiCanvasGenerationCompletionStatus]);


    //Template generation status
    useEffect(() => {
        if (aiTemplateGenerationProgress === AI_GENERATION_PROGRESS.IN_PROGRESS) {
            const toastID = renderInProgressToast("Generating interview templates. We'll let you know when they're ready!")
            setTemplateInProgressSnackbarID(toastID);
        }
    }, [aiTemplateGenerationProgress]);

    useEffect(() => {
        if (
            aiTemplateGenerationCompletionStatus ===
            AI_GENERATION_COMPLETION_STATUS.FAILED
        ) {
            setTemplateGenErrorModalOpen(true);
        } else if (
            aiTemplateGenerationCompletionStatus ===
            AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
        ) {
            setTemplateGenSuccessModalOpen(true);
        }

        if (
            aiTemplateGenerationCompletionStatus !==
            AI_GENERATION_COMPLETION_STATUS.NO_STATUS
        ) {
            setTimeout(() => toast.dismiss(templateInProgressSnackbarID), 500);
        }
    }, [aiTemplateGenerationCompletionStatus]);

    useEffect(() => {
        setAiCanvasGenerationState(prevState => {
            const newState = {...prevState}

            delete newState[teamID];

            return newState
        });

        setAiTemplateGenerationState(prevState => {
            const newState = {...prevState}

            delete newState[teamID];

            return newState
        });   
    }, [teamID])

    return (
      <>
        <AlertDialog
          dialogWidth="400px"
          open={canvasErrorModalOpen}
          onClose={handleCanvasErrorModalClose}
          title="Unable to create canvas. Please reload and try again."
          confirmButton={
            <IwButton variant="outlined" onClick={handleCanvasErrorModalClose}>
              Dismiss
            </IwButton>
          }
        />

        <AlertDialog
          dialogWidth="400px"
          open={canvasSuccessModalOpen}
          onClose={handleCanvasSuccessModalClose}
          title="Your canvas is ready!"
          confirmButton={
            <IwButton
              variant="outlined"
              onClick={handleCanvasSuccessModalClose}
            >
              Dismiss
            </IwButton>
          }
        />

        <AlertDialog
          dialogWidth="400px"
          open={templateGenErrorModalOpen}
          onClose={handleTemplateGenErrorModalClose}
          title="Oops, something went wrong! Please reload and try again!"
          confirmButton={
            <IwButton
              variant="outlined"
              onClick={handleTemplateGenErrorModalClose}
            >
              Dismiss
            </IwButton>
          }
        />

        <AlertDialog
          dialogWidth="400px"
          open={templateGenSuccessModalOpen}
          onClose={handleTemplateGenSuccessModalClose}
          title="Your interview templates are ready!"
          confirmButton={
            <IwButton
              variant="outlined"
              onClick={handleTemplateGenSuccessModalClose}
            >
              Dismiss
            </IwButton>
          }
        />
      </>
    );
};

export default AiGenerationStatus;
